import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import { MotionDiv } from 'motion';
import Button from '@ui/Button';
import SafeImage from '@ui/SafeImage';
import { getPublicUserDoc, getUserById } from '@util/firestore/users';
import { getProductById } from '@util/firestore/products';
import { getPaymentMethods } from '@util/firestore/payments';
import { updateOffer } from '@util/firestore/offers';
import { calculateEarningsFromOffer } from '@util/firestore/offers';
import { cn, formatCurrency, isMobile } from '@util/index';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import type { ChatMessageDocument } from '@models/chat';
import type { PublicUserDocument } from '@models/user';
import type { OfferDocument } from '@models/offer';
import type { PaymentDocument } from '@models/payment';
import OfferOperationModal from '@c/dashboard/OfferOperation/OfferOperationModal';
import MobileOfferOperationModal from '@c/dashboard/OfferOperation/MobileOfferOperationModal';
import { formatTimeRemaining } from '@c/dashboard/MessageOffer';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@c/sheet';
import { ChevronRightIcon } from '@c/icons';

interface MessageOfferProps {
  message: ChatMessageDocument;
  messageUser?: PublicUserDocument | null;
  offer: OfferDocument | null;
  isOldOffer: boolean;
  isOutbound: boolean;
}

const getBadgeInfo = (offer: OfferDocument, isLastMessage: boolean = false) => {
  if (offer.is_counter && !isLastMessage) {
    return {
      bg: '#F1F1F1',
      foreground: '#444444',
      text: 'Countered',
    };
  }

  // pending:0, accepted:1, declined:2, expired:3
  if (offer.state === 3) {
    return {
      bg: '#F1F1F1',
      foreground: '#444444',
      text: 'Expired',
    };
  }

  switch (offer.state) {
    case 1:
      return {
        bg: '#1E7A1E1A',
        foreground: '#1E7A1E',
        text: 'Accepted',
      };
    case 2:
      return {
        bg: '#F9E8E8',
        foreground: '#C41719',
        text: 'Declined',
      };
    case 3:
      return {
        bg: '#F1F1F1',
        foreground: '#444444',
        text: 'Expired',
      };
    default:
      return {
        bg: '#F1F1F1',
        foreground: '#444444',
        text: 'Pending',
      };
  }
};

function UpdatePaymentOptions({
  selectedPaymentMethod,
  offer_id,
}: {
  selectedPaymentMethod?: string;
  offer_id: string;
}) {
  const { data: paymentMethodData } = useQuery({
    queryKey: ['paymentMethods'],
    queryFn: getPaymentMethods,
  });
  const { showErrorToast, showSuccessToast } = useToastContext();

  async function updateOfferPayment(method: PaymentDocument) {
    if (method.id === selectedPaymentMethod) {
      showErrorToast('You already selected this payment method.');
      return;
    }
    try {
      await updateOffer(offer_id, {
        state: 0,
        failed: false,
        payment: {
          id: method.id,
          name: method.name,
          brand: method.brand,
          last4: method.last4,
          type: 0,
          saved: true,
        },
      });
      showSuccessToast('Payment method updated');
    } catch (e) {
      showErrorToast((e as Error).message);
    }
  }

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button type="tertiary" text="Update Payment Method" />
      </SheetTrigger>
      <SheetContent
        side={isMobile() ? 'bottom' : 'right'}
        className="z-[100] bg-white text-[1.8rem] text-black sm:min-w-[50rem]"
      >
        <SheetHeader>
          <SheetTitle>Update Payment Method</SheetTitle>
        </SheetHeader>
        <div className="flex w-full flex-col ">
          {paymentMethodData?.map((payment) => {
            return (
              <button
                type="button"
                className="mx-auto flex w-full items-center gap-4 border-b-[1px] border-gray-400 p-8"
                key={payment.id}
                onClick={() => updateOfferPayment(payment)}
              >
                <SafeImage
                  src={`/cc_brands/${payment.brand?.toLowerCase()}.png`}
                  alt={payment.brand ?? ''}
                  width={64}
                  height={48}
                  className="h-18 mr-2 w-28 object-contain"
                />
                <div className="flex flex-col items-start gap-2">
                  <div className="text-[2.2rem] font-semibold">
                    {payment.name}
                  </div>
                  <div>{payment.last4}</div>
                </div>
                <div className="flex grow items-center justify-end gap-4 text-brand-mid-gray">
                  {selectedPaymentMethod === payment.id && (
                    <span>Selected</span>
                  )}
                  <ChevronRightIcon />
                </div>
              </button>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function MessageOffer({
  message,
  messageUser,
  offer,
  isOldOffer,
  isOutbound,
}: MessageOfferProps) {
  const [collapsed, setCollapsed] = useState(true);
  const [acceptModalVisible, setAcceptModalVisible] = useState(false);
  const [counterModalVisible, setCounterModalVisible] = useState(false);
  const [declineModalVisible, setDeclineModalVisible] = useState(false);
  const { userDoc } = useAuth();
  const isSupport = !!userDoc?.roles?.support && !userDoc.roles.admin;

  const { data: product } = useQuery({
    queryKey: ['product', offer?.product_id],
    queryFn: () => getProductById(offer?.product_id ?? ''),
    enabled: !!offer?.product_id,
  });

  const { data: seller } = useQuery({
    queryKey: ['seller', offer?.seller_id],
    queryFn: () => getUserById(offer?.seller_id ?? ''),
    enabled: !!offer?.seller_id,
  });

  const { data: buyerDocument } = useQuery({
    queryKey: ['publicUser', offer?.buyer_id],
    queryFn: () => getPublicUserDoc({ uid: offer?.buyer_id ?? '' }),
    enabled: !!offer?.buyer_id,
  });
  const { data: sellerDocument } = useQuery({
    queryKey: ['publicUser', offer?.seller_id],
    queryFn: () => getPublicUserDoc({ uid: offer?.seller_id ?? '' }),
    enabled: !!offer?.seller_id,
  });

  const getNetEarnings = (
    offerData: Pick<
      OfferDocument,
      'total' | 'shipping_cost' | 'rate_id' | 'price' | 'address' | 'tax'
    >
  ) => {
    if (!product) return 0;
    return calculateEarningsFromOffer(
      offerData,
      seller?.tax_states ?? [],
      product
    );
  };

  if (!offer || !userDoc) return null;

  const isSender = message.uid === userDoc.uid;
  const offerType = message.content.includes('Counter')
    ? 'Counter'
    : message.content.includes('Exclusive')
    ? 'Exclusive'
    : '';
  const badgeInfo = getBadgeInfo(offer, !isOldOffer);
  const isTalkingToDeletedUser =
    sellerDocument?.username.startsWith('deleted-') ||
    buyerDocument?.username.startsWith('deleted-');

  return (
    <div
      className={`mb-8 flex w-fit flex-col ${
        isOutbound ? 'ml-auto pr-4' : 'mr-auto'
      }`}
    >
      <div className="w-[38rem] space-y-8 rounded-[1.5rem] border border-zinc-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          {userDoc.username === messageUser?.username ? (
            <span className="text-[1.8rem] font-semibold text-zinc-800">
              {offerType} Offer to{' '}
              <span className={cn(isTalkingToDeletedUser && 'text-red-600')}>
                {isTalkingToDeletedUser
                  ? 'Deleted User'
                  : buyerDocument?.uid === userDoc.uid
                  ? sellerDocument?.username
                  : buyerDocument?.username}
              </span>
            </span>
          ) : (
            <span className="text-[1.8rem] font-semibold text-zinc-800">
              {offerType} Offer from {messageUser?.username}
            </span>
          )}

          {offer.state === 0 && !isSender && !isSupport && !isOldOffer && (
            <button
              type="button"
              className="border-none bg-inherit outline-none"
              onClick={() => {
                setDeclineModalVisible(true);
              }}
            >
              <span className="text-[1.6rem] font-bold text-brand-secondary">
                Decline
              </span>
            </button>
          )}
        </div>

        <div className="mt-8 flex items-center gap-x-6">
          <h2 className="text-[2.4rem] font-bold text-black">
            {isOldOffer
              ? `Countered at ${formatCurrency(
                  message.offer_amount ?? offer.price
                )}`
              : formatCurrency(offer.price)}
          </h2>

          {!isOldOffer && (
            <button
              type="button"
              className="border-none bg-inherit outline-none"
              onClick={() => setCollapsed(!collapsed)}
            >
              <ChevronDownIcon
                className={`h-8 w-8 text-zinc-400 ${
                  collapsed ? '' : 'rotate-180'
                }`}
              />
            </button>
          )}
        </div>

        <AnimatePresence initial={false}>
          {!collapsed && !isOldOffer && (
            <MotionDiv
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={{
                open: { opacity: 1, height: 'auto' },
                collapsed: { opacity: 0, height: 0 },
              }}
              transition={{ duration: 0.1, ease: 'easeInOut' }}
            >
              <MotionDiv
                variants={{ collapsed: { scale: 0.8 }, open: { scale: 1 } }}
                transition={{ duration: 0.1 }}
                className="flex flex-col gap-y-4"
              >
                <div className="flex items-center gap-x-2">
                  <span className="text-[1.8rem] font-medium text-zinc-500">
                    Offer Price:
                  </span>
                  <span className="text-[1.8rem] font-semibold text-zinc-600">
                    {formatCurrency(offer.price)}
                  </span>
                </div>

                {!!offer.shipping_cost && (
                  <div className="flex items-center gap-x-2">
                    <span className="text-[1.8rem] font-medium text-zinc-500">
                      Shipping:
                    </span>
                    <span className="text-[1.8rem] font-semibold text-zinc-600">
                      {formatCurrency(offer.shipping_cost)}
                    </span>
                  </div>
                )}

                {!!offer.tax && (
                  <div className="flex items-center gap-x-2">
                    <span className="text-[1.8rem] font-medium text-zinc-500">
                      Tax:
                    </span>
                    <span className="text-[1.8rem] font-semibold text-zinc-600">
                      {formatCurrency(offer.tax)}
                    </span>
                  </div>
                )}

                <div className="flex items-center gap-x-2 border-b border-t border-zinc-300 py-4">
                  <span className="text-[1.8rem] font-medium text-zinc-500">
                    Total:
                  </span>
                  <span className="text-[1.8rem] font-semibold text-zinc-600">
                    {formatCurrency(offer.total)}
                  </span>
                </div>

                {product && offer.seller_id === userDoc?.uid && (
                  <div className="flex items-center gap-x-2">
                    <span className="text-[1.8rem] font-bold text-brand-black">
                      Net earnings:
                    </span>
                    <span className="text-[1.8rem] font-bold text-brand-black">
                      {formatCurrency(getNetEarnings(offer))}
                    </span>
                  </div>
                )}
              </MotionDiv>
            </MotionDiv>
          )}
        </AnimatePresence>

        <div className="flex flex-col gap-y-8">
          {offer.time_remaining &&
            offer.state !== 2 &&
            new Date(offer.time_remaining) > new Date() &&
            formatTimeRemaining(offer) !== '0d 0h' &&
            !isOldOffer && (
              <div className="flex items-center gap-x-2">
                <span className={'text-[1.5rem] font-medium text-zinc-700'}>
                  Expires in {formatTimeRemaining(offer)}
                </span>
              </div>
            )}

          {offer.state === 0 &&
          !isSupport &&
          formatTimeRemaining(offer) !== '0d 0h' ? (
            <div className="flex w-full items-center gap-x-4">
              {!isSender && !offer.is_exclusive && !isOldOffer && (
                <button
                  type="button"
                  className="flex w-full items-center justify-center rounded-[1.5rem] border border-brand-black bg-inherit p-4 font-semibold text-brand-black"
                  onClick={() => {
                    setCounterModalVisible(true);
                  }}
                >
                  Counter
                </button>
              )}

              {!isSender && !isOldOffer ? (
                <button
                  type="button"
                  className="flex w-full items-center justify-center rounded-[1.5rem] bg-[#1E7A1E] p-4 font-semibold text-white"
                  onClick={() => {
                    setAcceptModalVisible(true);
                  }}
                >
                  Accept
                </button>
              ) : isOldOffer ? (
                <div className="flex w-full items-center justify-center rounded-[1.5rem] bg-[#F1F1F1] p-4 font-semibold text-[#444444]">
                  Countered at{' '}
                  {formatCurrency(message.offer_amount ?? offer.price)}
                </div>
              ) : (
                <div className="flex w-full items-center justify-center rounded-[1.5rem] bg-[#F1F1F1] p-4 font-semibold text-[#444444]">
                  Pending
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="flex w-full items-center gap-x-4">
                <div
                  className="rounded-full px-6 py-2 font-semibold"
                  style={{
                    backgroundColor: badgeInfo.bg,
                    color: badgeInfo.foreground,
                  }}
                >
                  {badgeInfo.text}
                </div>
              </div>
              {offer.state === 2 &&
                offer.failed &&
                offer.buyer_id === userDoc?.uid && (
                  <UpdatePaymentOptions
                    offer_id={offer.id}
                    selectedPaymentMethod={offer.payment.id}
                  />
                )}
            </>
          )}
        </div>

        {isMobile() ? (
          <>
            <MobileOfferOperationModal
              show={acceptModalVisible}
              dismiss={() => setAcceptModalVisible(false)}
              type="accept"
              offer={offer}
            />

            <MobileOfferOperationModal
              show={counterModalVisible}
              dismiss={() => setCounterModalVisible(false)}
              type="counter"
              offer={offer}
            />

            <MobileOfferOperationModal
              show={declineModalVisible}
              dismiss={() => setDeclineModalVisible(false)}
              type="decline"
              offer={offer}
            />
          </>
        ) : (
          <>
            <OfferOperationModal
              isOpen={acceptModalVisible}
              setIsOpen={setAcceptModalVisible}
              type="accept"
              onClose={() => setAcceptModalVisible(false)}
              offer={offer}
            />

            <OfferOperationModal
              isOpen={counterModalVisible}
              setIsOpen={setCounterModalVisible}
              type="counter"
              onClose={() => setCounterModalVisible(false)}
              offer={offer}
            />

            <OfferOperationModal
              isOpen={declineModalVisible}
              setIsOpen={setDeclineModalVisible}
              type="decline"
              onClose={() => setDeclineModalVisible(false)}
              offer={offer}
            />
          </>
        )}
      </div>

      {Boolean(message.created_at) && (
        <p
          className={`mt-4 text-[1.5rem] text-brand-gray ${
            isOutbound ? 'self-end' : 'self-start'
          }`}
        >
          {new Date(message.created_at).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      )}
    </div>
  );
}
